import React, { useState, useRef, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import AceEditor from 'react-ace';
import AdminBar from '../components/AdminBar';
import { Maximize, Minimize, Undo2, Redo2, Square } from 'lucide-react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RestoreIcon from '@mui/icons-material/Restore';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import './Chat.css';
import ConsolePane from '../components/ConsolePane';
import { useThemeMode } from '../components/ThemeModeContext';
import axios from 'axios';
import { serverDomain } from './config';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-tomorrow_night';
import 'ace-builds/src-noconflict/theme-chaos';


//const defaultHtmlContent = '<!doctype html><html><head><meta charset="utf-8"><title>AppStar</title></head><body></body></html>';

const defaultHtmlContent = `<!DOCTYPE html>
	<html lang="en">
<head>
	<meta charset="UTF-8">
	<meta name="viewport" content="width=device-width, initial-scale=1.0">
	<title>Your App</title>
	<style>
		body, h1, p, div { font-family: Arial, sans-serif; color: #7070ff;}
		.center-content {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
		}
	</style>
</head>
<body class="bg-gray-100 flex items-center justify-center min-h-screen">
	<div class="bg-white p-6 rounded-lg shadow-lg max-w-md center-content">
		<h1 class="text-3xl font-bold mb-4">Welcome to AppStar!</h1>
		<img src="appstar_title.jpg" alt="AppStar Logo" class="mx-auto mb-4" style="max-width: 50%; height: auto;">
		<p class="text-gray-600">This is where your generated app will appear.</p>
	</div>
</body>
</html>`;

const initialMessages = [];

function SourceView({ theme, isDarkMode, viewingSource, sourceBuffer, setSourceBuffer, sourceEditorRef, isEditingRef }) {
  const keyTimerRef = useRef(null);
  const stickySourceFocusRef = useRef(false);
  const externalFocusIntentRef = useRef(false);
  const editorKeydownCleanupRef = useRef(null);

  // Cleanup timer only on unmount
  useEffect(() => {
    return () => {
      if (keyTimerRef.current) {
        clearTimeout(keyTimerRef.current);
        keyTimerRef.current = null;
      }
      if (editorKeydownCleanupRef.current) {
        editorKeydownCleanupRef.current();
        editorKeydownCleanupRef.current = null;
      }
    };
  }, []);

  // Track intentional focus moves (click/tap outside the source editor).
  useEffect(() => {
    const onPointerDownCapture = (ev) => {
      try {
        const editor = sourceEditorRef.current;
        const container = editor && editor.container;
        externalFocusIntentRef.current = !!(container && ev.target && !container.contains(ev.target));
      } catch (_) {}
    };
    document.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => { document.removeEventListener('pointerdown', onPointerDownCapture, true); };
  }, [sourceEditorRef]);

  const shouldRestoreSourceFocus = (nextActiveEl) => {
    try {
      const editor = sourceEditorRef.current;
      const container = editor && editor.container;
      if (!viewingSource || !container) return false;
      if (!stickySourceFocusRef.current) return false;
      if (externalFocusIntentRef.current) return false;
      // Do not steal focus from another concrete control (chat input, admin fields, dropdowns, etc.).
      if (nextActiveEl && nextActiveEl !== document.body && !container.contains(nextActiveEl)) return false;
      return true;
    } catch (_) {
      return false;
    }
  };

  const restartKeyTimer = () => {
    console.log('Restarting key timer');
    try {
      if (keyTimerRef.current) clearTimeout(keyTimerRef.current);
      // Helper to read the latest source text at callback time
      const getCurrentSourceText = () => {
        try {
          const editor = sourceEditorRef.current;
          const txt = editor ? editor.getValue() : (sourceBuffer || '');
          return String(txt).replace(/\r\n/g, '\n');
        } catch (_) { return String(sourceBuffer || ''); }
      };

      // Save current source to server as JSON after idle
      const saveSourceAfterIdle = () => {
        try {
          const app_id = localStorage.getItem('appstar_app_id');
          const token = localStorage.getItem('appstar_jwt');
          const htmlsource = getCurrentSourceText();
          const url = serverDomain + 'save-source-code.php';
          console.log('Saving source after idle', { url, app_id, hasToken: !!token, length: htmlsource.length });
          axios.post(
            url, 
            { htmlsource, app_id},
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          )
          .then((res) => { try { console.log('save-source-code.php success', res?.status, res?.data); } catch (_) {} })
          .catch((err) => { try { console.error('save-source-code.php error', err?.response?.status, err?.message, err?.response?.data); } catch (_) {} });
        } catch (err) {
          console.error('saveSourceAfterIdle failed', err);
        }
      };

      keyTimerRef.current = setTimeout(() => {
        try { console.log("Timer end"); saveSourceAfterIdle(); } catch (_) {}
      }, 3000);
    } catch (_) {}
  };

  const handleEditorLoad = (editor) => {
    sourceEditorRef.current = editor;

    if (editorKeydownCleanupRef.current) {
      editorKeydownCleanupRef.current();
      editorKeydownCleanupRef.current = null;
    }

    try {
      if (editor && editor.commands) {
        editor.commands.removeCommand('showSettingsMenu');
      }
    } catch (_) {}

    try {
      if (editor && editor.setOption) {
        editor.setOption('enableMobileMenu', false);
      }
    } catch (_) {}

    try {
      const container = editor.container;
      const onKeyDown = (ev) => {
        isEditingRef.current = true;
        if ((ev.ctrlKey || ev.metaKey) && ev.key.length === 1) ev.stopPropagation();
        restartKeyTimer();
      };
      container.addEventListener('keydown', onKeyDown, true);
      editorKeydownCleanupRef.current = () => {
        try { container.removeEventListener('keydown', onKeyDown, true); } catch (_) {}
      };
    } catch (_) {}
  };

  const handleEditorChange = (nextValue) => {
    try {
      if (!isEditingRef.current) return;
      const normalized = String(nextValue || '').replace(/\r\n/g, '\n');
      setSourceBuffer(normalized);
      restartKeyTimer();
      requestAnimationFrame(() => {
        try {
          const editor = sourceEditorRef.current;
          if (stickySourceFocusRef.current && editor && document.activeElement === document.body) {
            editor.focus();
          }
        } catch (_) {}
      });
    } catch (_) {}
  };

  const applyHistoryAction = (action) => {
    try {
      const editor = sourceEditorRef.current;
      if (!editor) return;
      try { editor.focus(); } catch (_) {}
      try {
        if (action === 'undo') editor.undo();
        if (action === 'redo') editor.redo();
      } catch (_) {}
      try {
        const txt = String(editor.getValue() || '').replace(/\r\n/g, '\n');
        setSourceBuffer(txt);
      } catch (_) {}
      restartKeyTimer();
    } catch (_) {}
  };

  const aceTheme = isDarkMode ? 'chaos' : 'github';

  return (
    <div style={{ height: '100%', width: '100%', background: theme.sourceContainerBg, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: 12, borderBottom: `1px solid ${theme.border}`, color: theme.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontWeight: 'bold' }}>HTML Source - {sourceBuffer ? sourceBuffer.length + ' characters' : ''}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
          <button
            type="button"
            title="Undo"
            aria-label="Undo"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyHistoryAction('undo')}
            style={{ width: 34, height: 34, border: `1px solid ${theme.buttonBorder || theme.border}`, borderRadius: 6, background: theme.buttonBg, color: theme.buttonText, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
          >
            <Undo2 size={16} />
          </button>
          <button
            type="button"
            title="Redo"
            aria-label="Redo"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyHistoryAction('redo')}
            style={{ width: 34, height: 34, border: `1px solid ${theme.buttonBorder || theme.border}`, borderRadius: 6, background: theme.buttonBg, color: theme.buttonText, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
          >
            <Redo2 size={16} />
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', padding: 0, background: theme.sourceContainerBg, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, minHeight: 0, border: `1px solid ${theme.border}`, borderRadius: 0, overflow: 'hidden', background: theme.sourceBg }}>
          <AceEditor
            mode="html"
            theme={aceTheme}
            name="appstar-source-editor"
            width="100%"
            height="100%"
            value={sourceBuffer || ''}
            onLoad={handleEditorLoad}
            onChange={handleEditorChange}
            onFocus={() => { isEditingRef.current = true; stickySourceFocusRef.current = true; externalFocusIntentRef.current = false; }}
            onBlur={() => {
              isEditingRef.current = false;
              requestAnimationFrame(() => {
                try {
                  const nextActiveEl = document.activeElement;
                  if (!shouldRestoreSourceFocus(nextActiveEl)) return;
                  const editor = sourceEditorRef.current;
                  if (editor && document.activeElement === document.body) {
                    editor.focus();
                    isEditingRef.current = true;
                  }
                } catch (_) {}
              });
            }}
            fontSize={14}
            showGutter
            showPrintMargin={false}
            highlightActiveLine={false}
            highlightGutterLine
            wrapEnabled={false}
            setOptions={{
              tabSize: 2,
              useSoftTabs: true,
              useWorker: false,
              enableMobileMenu: false,
              enableLiveAutocompletion: true,
              displayIndentGuides: true,
              highlightIndentGuides: true,
              scrollPastEnd: 0,
              hScrollBarAlwaysVisible: true,
              vScrollBarAlwaysVisible: true,
              highlightMatchingBrackets: true,
              highlightMatchingTags: true,
            }}
            editorProps={{ $blockScrolling: true }}
            style={{ height: '100%', width: '100%', fontFamily: 'monospace', background: theme.sourceBg, color: theme.text }}
          />
        </div>
      </div>
    </div>
  );
}

function PreviewPane({ injectedHtml, iframeKey, previewBg, onIframeLoad }) {
  return (
    <div style={{ height: '100%', width: '100%', background: previewBg, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {injectedHtml ? (
          <iframe key={iframeKey} srcDoc={injectedHtml} title="HTML Preview" style={{ width: '100%', height: '100%', border: 'none', minHeight: 300 }} sandbox="allow-scripts allow-modals allow-same-origin" tabIndex={-1} onLoad={onIframeLoad} />
        ) : (
          <div style={{ color: '#888', textAlign: 'center' }}>Ask the AI to create HTML code and it will appear here.</div>
        )}
      </div>
    </div>
  );
}

// Converts get-sources / restore-to-source rows into chat messages carrying their source_id
function mapSourcesToMessages(chatMessages) {
  const base = Date.now();
  const mapped = [];
  chatMessages.forEach((m, idx) => {
    const input = typeof m.chatInput === 'string' ? m.chatInput : '';
    const response = typeof m.chatResponse === 'string' ? m.chatResponse : '';
    const sourceId = Number.isInteger(m.sourceId) ? m.sourceId : null;
    if (input) mapped.push({ id: base + idx * 2, sender: 'user', content: input.replace(/\r?\n/g, '<br>'), htmlCode: '', sourceId });
    if (response) mapped.push({ id: base + idx * 2 + 1, sender: 'ai', content: response.replace(/\r?\n/g, '<br>'), htmlCode: '', sourceId });
  });
  return mapped;
}

// Memoized ChatPane exposing an imperative sendMessage(text) for parent to trigger sends
const ChatPane = React.memo(React.forwardRef(function ChatPane({
  setPreviewHtml,
  showRainbowAnimation,
  inputRef,
  messages,
  setMessages,
  idCounterRef,
  previewHtml,
  sourceBuffer,
  setSourceBuffer,
  firstSend,
  setFirstSend,
  setIsLoading,
  isLoading,
  setIframeKey,
  isMobile,
  isAppleMobile,
  isKeyboardOpen,
  setMobileView,
  theme,
  apiTimer,
  isDarkMode,
}, ref) {
  const [currentMessage, setCurrentMessage] = useState('');
  const textareaRef = useRef(null);
  const chatContainerRef = useRef(null);
  const composerRef = useRef(null);
  const isSendingRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const requestRunIdRef = useRef(0);
  const [activeAiMsgId, setActiveAiMsgId] = useState(null);
  const activeAiMsgIdRef = useRef(null);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [composerHeight, setComposerHeight] = useState(84);
  const isFirefoxMobile = isFirefoxMobileBrowser();
  const usesDynamicViewportLayout = isAppleMobile || usesDynamicViewportShell();
  const usesFixedBottomComposer = usesDynamicViewportLayout || isFirefoxMobileBrowser();
  const useFlowComposer = isMobile && isFirefoxMobile && !isKeyboardOpen;
  const usePinnedFirefoxComposer = isMobile && isFirefoxMobile && isKeyboardOpen;
  const composerPosition = usePinnedFirefoxComposer ? 'fixed' : (useFlowComposer ? 'relative' : 'absolute');
  const composerPadding = isFirefoxMobile
    ? '12px 12px 12px'
    : '12px 12px calc(12px + env(safe-area-inset-bottom, 0px))';

  const scrollChatToBottom = () => {
    try {
      const container = chatContainerRef.current;
      if (!container) return;
      container.scrollTop = container.scrollHeight;
    } catch (_) {}
  };

  const getMessagePlainText = (htmlContent) => {
    try {
      const parser = new DOMParser();
      const normalizedHtml = String(htmlContent || '').replace(/<br\s*\/?>/gi, '\n');
      const doc = parser.parseFromString(`<div>${normalizedHtml}</div>`, 'text/html');
      return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();
    } catch (_) {
      return String(htmlContent || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    }
  };

  const copyMessageText = async (htmlContent) => {
    const plainText = getMessagePlainText(htmlContent);
    if (!plainText) return;
    try {
      await navigator.clipboard.writeText(plainText);
    } catch (_) {
      try {
        const helper = document.createElement('textarea');
        helper.value = plainText;
        helper.setAttribute('readonly', 'true');
        helper.style.position = 'fixed';
        helper.style.opacity = '0';
        document.body.appendChild(helper);
        helper.select();
        document.execCommand('copy');
        document.body.removeChild(helper);
      } catch (_) {}
    }
  };

  useEffect(() => {
    let int = null;
    if (isLoading) {
      setLoadingSeconds(0);
      int = setInterval(() => setLoadingSeconds(s => s + 1), 1000);
    }
    return () => { if (int) clearInterval(int); };
  }, [isLoading]);

  useEffect(() => {
    if (!chatContainerRef.current) return undefined;
    scrollChatToBottom();
    const timeouts = [100, 300, 500].map(ms => setTimeout(() => scrollChatToBottom(), ms));
    return () => timeouts.forEach(clearTimeout);
  }, [messages]);

  useEffect(() => {
    if (!isKeyboardOpen || !chatContainerRef.current) return undefined;
    scrollChatToBottom();
    const timeouts = [50, 150, 300, 500].map(ms => setTimeout(() => scrollChatToBottom(), ms));
    return () => timeouts.forEach(clearTimeout);
  }, [isKeyboardOpen, composerHeight]);

  useEffect(() => {
    const composer = composerRef.current;
    if (!composer) return undefined;

    const syncComposerHeight = () => {
      try {
        const nextHeight = Math.ceil(composer.getBoundingClientRect().height || 0);
        if (nextHeight > 0) setComposerHeight(nextHeight);
      } catch (_) {}
    };

    syncComposerHeight();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => syncComposerHeight());
      resizeObserver.observe(composer);
    }

    window.addEventListener('resize', syncComposerHeight);
    return () => {
      window.removeEventListener('resize', syncComposerHeight);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [currentMessage, isMobile, isLoading]);

  useEffect(() => {
    return () => {
      stopRequestedRef.current = true;
      requestRunIdRef.current += 1;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (_) {}
        abortControllerRef.current = null;
      }
    };
  }, []);

  const setActiveStreamMessageId = (messageId) => {
    activeAiMsgIdRef.current = messageId;
    setActiveAiMsgId(messageId);
  };

  const replaceAiMessage = (messageId, content, htmlCode) => {
    const safeContent = String(content || '').replace(/\r?\n/g, '<br>');
    setMessages(prev => prev.map(m => (
      m.id === messageId
        ? {
            ...m,
            content: safeContent,
            ...(htmlCode === undefined ? {} : { htmlCode })
          }
        : m
    )));
  };

  const handleStopStream = () => {
    stopRequestedRef.current = true;
    requestRunIdRef.current += 1;

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      try { abortControllerRef.current.abort(); } catch (_) {}
      abortControllerRef.current = null;
    }

    const activeId = activeAiMsgIdRef.current;
    if (activeId !== null) {
      replaceAiMessage(activeId, 'Stopped.', '');
    }

    isSendingRef.current = false;
    activeAiMsgIdRef.current = null;
    setActiveAiMsgId(null);
    setIsLoading(false);
    setFirstSend(false);
  };

  const getFriendlyServerErrorMessage = (serverData, status, fallbackMessage) => {
    const serverMsg = typeof serverData === 'string'
      ? serverData
      : (serverData && (serverData.message || serverData.error || (serverData.data && (serverData.data.message || serverData.data.errorText)))) || '';

    const serverCode = serverData && (serverData.code || (serverData.data && serverData.data.code));
    const isSourceTooLarge =
      serverCode === 'APP_SOURCE_TOO_LARGE' ||
      /sources_htmlsource_check|app source is too large/i.test(String(serverMsg));

    if (isSourceTooLarge) {
      return 'Your app source is too large. Start a new app.';
    }

    if (typeof serverMsg === 'string' && serverMsg.trim()) {
      return serverMsg;
    }

    return fallbackMessage;
  };

  const handleSend2 = async (overrideText) => {
    const messageToSend = typeof overrideText === 'string' ? overrideText : currentMessage;
    if (!messageToSend.trim()) return;
    const contentWithBreaks = messageToSend.replace(/\n/g, '<br>');
    const userMsg = { id: idCounterRef.current++, sender: 'user', content: contentWithBreaks };
    // Append user message and clear the textarea immediately
    setMessages(prev => [...prev, userMsg]);
    setCurrentMessage('');
    if (textareaRef.current) {
      try { textareaRef.current.style.height = 'auto'; } catch (_) {}
      // On mobile, avoid refocusing by blurring the textarea after send
      try { if (isMobile) textareaRef.current.blur(); } catch (_) {}
    }

    const app_id = localStorage.getItem('appstar_app_id');
    const token = localStorage.getItem('appstar_jwt');
    const url = serverDomain + 'generate5.php';

    setIsLoading(true);

    try {
      const res = await axios.post(
        url,
        null,
        {
          params: {
            chatinput: messageToSend,
            htmlsource: firstSend ? '' : (previewHtml || ''),
            app_id,
            token
          }
        }
      );

      console.log('generate5.php response', res);

      const firstNonEmptyString = (...vals) => {
        for (const v of vals) {
          if (typeof v === 'string' && v.trim().length > 0) return v;
        }
        return '';
      };
      const dataNode = res?.data?.data ?? res?.data ?? {};
      const history = Array.isArray(dataNode?.chatHistory)
        ? dataNode.chatHistory
        : Array.isArray(dataNode?.chat_messages)
        ? dataNode.chat_messages
        : Array.isArray(dataNode?.history)
        ? dataNode.history
        : [];

      let aiText = '';
      for (let i = history.length - 1; i >= 0; i--) {
        const item = history[i] || {};
        aiText = firstNonEmptyString(
          item.chatR,
          item.chatResponse,
          item.chatresponse,
          item.response,
          item.ai,
          item.text,
        );
        if (aiText) break;
      }
      if (!aiText) {
        aiText = firstNonEmptyString(
          dataNode.chatResponse,
          dataNode.chatresponse,
          dataNode.response,
          dataNode.ai,
          dataNode.text,
        );
      }

      let html = firstNonEmptyString(
        dataNode.htmlSource,
        dataNode.htmlsource,
        dataNode.html_code,
        dataNode.html,
        res?.data?.htmlSource,
        res?.data?.htmlsource,
      );

      //console.log("html from server:", { html, length: typeof html === 'string' ? html.length : 'n/a' });

      if (html) {
        html = String(html).replace(/\r\n/g, '\n');
 
      }

      //console.log("html from server \n converted to cr char:", { html, length: typeof html === 'string' ? html.length : 'n/a' });

      const aiMsg = {
        id: idCounterRef.current++,
        sender: 'ai',
        content: (aiText || '').replace(/\r?\n/g, '<br>'),
        htmlCode: html,
      };

      setMessages(prev => [...prev, aiMsg]);

      setPreviewHtml(html || previewHtml || '');
      setSourceBuffer(html || sourceBuffer || '');
      setIframeKey(prev => prev + 1);
    } catch (err) {
        /*    OLD ERROR CODE
              const aiMsg = { id: idCounterRef.current++, sender: 'ai', content: 'An error occurred. Please try again.' + (err?.message ? (' F/E Error: ' + err.message) : '') };
              setMessages(prev => [...prev, aiMsg]);
        */    

      //Better error handling with HTTP status code and server response data
      const status = err?.response?.status;
      const serverData = err?.response?.data;
      //if serverData contains an error message, that contains the text "SQLSTATE[23514]" it means that a constraint has been violated - Probably htmlsource is >= 30, 000 characters
      console.error('generate5.php failed', {
        status,
        url,
        params: { chatinput: messageToSend, htmlsource: firstSend ? '' : (previewHtml || ''), app_id, token },
        serverData,
        err
      });
      // If the user has run out of credit, show an alert and stop
      try {
        const serverMsg = typeof serverData === 'string'
          ? serverData
          : (serverData && (serverData.message || serverData.error || (serverData.data && serverData.data.message))) || '';
        const insufficient = (status === 403) && /insufficient\s*credit/i.test(String(serverMsg));
        if (insufficient) {
          //make spinner, counter and overlay disappear before alert displays...
          try { flushSync(() => setIsLoading(false)); } catch (_) { setIsLoading(false); }
          // If Auto-fix is running in the ConsolePane, force it to stop.
          try { window.dispatchEvent(new Event('appstar-insufficient-credit')); } catch (_) {}
          try {
            if (typeof document !== 'undefined' && document.body) { void document.body.offsetHeight; }
            await new Promise((r) => requestAnimationFrame(() => r()));
            await new Promise((r) => setTimeout(r, 0));
          } catch (_) {}
          window.alert('Insufficient credit!\nGo to Account in menu to add credit.');
          return;
        }
      } catch (_) {}
      const friendlyMessage = getFriendlyServerErrorMessage(
        serverData,
        status,
        `An error occurred. Please try again.` +
        (status ? ` (HTTP ${status})` : '') +
        (err?.message ? (' F/E Error: ' + err.message) : '')
      );

      const aiMsg = {
        id: idCounterRef.current++,
        sender: 'ai',
        content: friendlyMessage
      };
      setMessages(prev => [...prev, aiMsg]);


    } finally {
      setIsLoading(false);
      setFirstSend(false);
      // textarea already cleared on send; ensure height reset remains sane
      if (textareaRef.current) {
        try { textareaRef.current.style.height = 'auto'; } catch (_) {}
      }
    }
  };


  const handleSendStream = async (overrideText) => {
    if (isSendingRef.current || isLoading) return;
    const messageToSend = typeof overrideText === 'string' ? overrideText : currentMessage;
    if (!messageToSend.trim()) return;
    const contentWithBreaks = messageToSend.replace(/\n/g, '<br>');
    const userMsg = { id: idCounterRef.current++, sender: 'user', content: contentWithBreaks };
    // Append user message and clear the textarea immediately
    setMessages(prev => [...prev, userMsg]);
    setCurrentMessage('');
    if (textareaRef.current) {
      try { textareaRef.current.style.height = 'auto'; } catch (_) {}
      // On mobile, avoid refocusing by blurring the textarea after send
      try { if (isMobile) textareaRef.current.blur(); } catch (_) {}
    }

    const app_id = localStorage.getItem('appstar_app_id');
    const token = localStorage.getItem('appstar_jwt');
    const startUrl = serverDomain + 'generate5_start.php';
    const tickUrl = serverDomain + 'generate5_tick.php';

    stopRequestedRef.current = false;
    const runId = requestRunIdRef.current + 1;
    requestRunIdRef.current = runId;
    isSendingRef.current = true;
    setIsLoading(true);

    const aiMsgId = idCounterRef.current++;
    let streamedText = '';
    let completed = false;
    setActiveStreamMessageId(aiMsgId);

    setMessages(prev => [...prev, { id: aiMsgId, sender: 'ai', content: '', htmlCode: '' }]);

    try {
      const wasStopped = () => (
        stopRequestedRef.current ||
        requestRunIdRef.current !== runId
      );

      const createAbortController = () => {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        return controller;
      };

      const body = new URLSearchParams({
        chatinput: messageToSend,
        htmlsource: firstSend ? '' : (previewHtml || ''),
        app_id: String(app_id || ''),
        token: String(token || '')
      });

      const startController = createAbortController();
      const startRes = await fetch(startUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        },
        body: body.toString(),
        signal: startController.signal
      });
      if (abortControllerRef.current === startController) {
        abortControllerRef.current = null;
      }
      if (wasStopped()) return;

      const startRaw = await startRes.text();
      let startJson = null;
      try { startJson = JSON.parse(startRaw); } catch (_) {}

      if (!startRes.ok || !startJson || startJson.success !== 1) {
        let parsedError = null;
        try { parsedError = JSON.parse(startRaw); } catch (_) {}
        throw {
          httpStatus: startRes.status,
          serverData: parsedError || startRaw,
          message: `HTTP ${startRes.status}`
        };
      }

      const jobId = startJson && startJson.data ? startJson.data.jobId : null;
      if (!jobId) {
        throw {
          httpStatus: 500,
          serverData: startJson,
          message: 'Backend did not return job id.'
        };
      }

      const updateAiMessageText = (txt) => {
        replaceAiMessage(aiMsgId, txt);
      };

      const applyFinalPayload = (payload) => {
        const firstNonEmptyString = (...vals) => {
          for (const v of vals) {
            if (typeof v === 'string' && v.trim().length > 0) return v;
          }
          return '';
        };

        const dataNode = payload?.data ?? payload ?? {};
        const history = Array.isArray(dataNode?.chatHistory)
          ? dataNode.chatHistory
          : Array.isArray(dataNode?.chat_messages)
          ? dataNode.chat_messages
          : Array.isArray(dataNode?.history)
          ? dataNode.history
          : [];

        let aiText = '';
        for (let i = history.length - 1; i >= 0; i--) {
          const item = history[i] || {};
          aiText = firstNonEmptyString(
            item.chatR,
            item.chatResponse,
            item.chatresponse,
            item.response,
            item.ai,
            item.text,
          );
          if (aiText) break;
        }

        if (!aiText) {
          aiText = firstNonEmptyString(
            dataNode.chatResponse,
            dataNode.chatresponse,
            dataNode.response,
            dataNode.ai,
            dataNode.text,
          );
        }

        let html = firstNonEmptyString(
          dataNode.htmlSource,
          dataNode.htmlsource,
          dataNode.html_code,
          dataNode.html,
        );

        if (html) {
          html = String(html).replace(/\r\n/g, '\n');
        }

        const displayText = (aiText || streamedText || '').replace(/\r?\n/g, '<br>');
        const newSourceId = Number.isInteger(dataNode.sourceId) ? dataNode.sourceId : null;
        setMessages(prev => prev.map(m => {
          if (m.id === aiMsgId) return { ...m, content: displayText, htmlCode: html, sourceId: newSourceId };
          if (m.id === userMsg.id) return { ...m, sourceId: newSourceId };
          return m;
        }));

        setPreviewHtml(html || previewHtml || '');
        setSourceBuffer(html || sourceBuffer || '');
        setIframeKey(prev => prev + 1);
      };

      updateAiMessageText('Queued...');

      const sleep = (ms) => new Promise((resolve) => {
        pollTimeoutRef.current = setTimeout(() => {
          pollTimeoutRef.current = null;
          resolve();
        }, ms);
      });
      const maxPolls = 360;
      const pollDelayMs = 2000;
      let transientEmpty500Count = 0;

      for (let attempt = 0; attempt < maxPolls; attempt++) {
        if (wasStopped()) return;
        await sleep(pollDelayMs);
        if (wasStopped()) return;

        const tickBody = new URLSearchParams({
          job_id: String(jobId),
          token: String(token || '')
        });

        const tickController = createAbortController();
        const tickRes = await fetch(tickUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
          },
          body: tickBody.toString(),
          signal: tickController.signal
        });
        if (abortControllerRef.current === tickController) {
          abortControllerRef.current = null;
        }
        if (wasStopped()) return;

        const tickRaw = await tickRes.text();
        let tickJson = null;
        try { tickJson = JSON.parse(tickRaw); } catch (_) {}

        if (!tickRes.ok || !tickJson || tickJson.success !== 1) {
          const isEmptyBody = !tickRaw || String(tickRaw).trim() === '';
          if (tickRes.status === 500 && isEmptyBody) {
            transientEmpty500Count += 1;
            if (transientEmpty500Count <= 3) {
              updateAiMessageText('Running...');
              continue;
            }
          }
          throw {
            httpStatus: tickRes.status,
            serverData: tickJson || tickRaw,
            message: `HTTP ${tickRes.status}`
          };
        }

        transientEmpty500Count = 0;

        const tickData = tickJson.data || {};
        const statusText = String(tickData.jobStatus || '').toLowerCase();
        const progressText = typeof tickData.progressText === 'string' ? tickData.progressText : '';

        if (statusText === 'completed') {
          streamedText = tickData.chatResponse || '';
          if (wasStopped()) return;
          applyFinalPayload({ data: tickData });
          completed = true;
          break;
        }

        if (statusText === 'failed') {
          throw {
            httpStatus: 500,
            serverData: tickJson,
            message: tickData.errorText || tickJson.message || 'Generation failed.'
          };
        }

        updateAiMessageText(progressText || 'Running...');
      }

      if (!completed) {
        throw {
          httpStatus: 504,
          serverData: null,
          message: 'Timed out while waiting for completion.'
        };
      }
    } catch (err) {
      const wasUserStop =
        err?.name === 'AbortError' ||
        stopRequestedRef.current ||
        requestRunIdRef.current !== runId;

      if (wasUserStop) {
        return;
      }

        /*    OLD ERROR CODE
              const aiMsg = { id: idCounterRef.current++, sender: 'ai', content: 'An error occurred. Please try again.' + (err?.message ? (' F/E Error: ' + err.message) : '') };
              setMessages(prev => [...prev, aiMsg]);
        */    

      //Better error handling with HTTP status code and server response data
      const status = err?.httpStatus || err?.response?.status;
      const serverData = err?.serverData || err?.response?.data;
      //if serverData contains an error message, that contains the text "SQLSTATE[23514]" it means that a constraint has been violated - Probably htmlsource is >= 30, 000 characters
      console.error('generate5 async flow failed', {
        status,
        startUrl,
        tickUrl,
        params: { chatinput: messageToSend, htmlsource: firstSend ? '' : (previewHtml || ''), app_id, token },
        serverData,
        err
      });
      // If the user has run out of credit, show an alert and stop
      try {
        const serverMsg = typeof serverData === 'string'
          ? serverData
          : (serverData && (serverData.message || serverData.error || (serverData.data && serverData.data.message))) || '';
        const insufficient = (status === 403) && /insufficient\s*credit/i.test(String(serverMsg));
        if (insufficient) {
          //make spinner, counter and overlay disappear before alert displays...
          try { flushSync(() => setIsLoading(false)); } catch (_) { setIsLoading(false); }
          // If Auto-fix is running in the ConsolePane, force it to stop.
          try { window.dispatchEvent(new Event('appstar-insufficient-credit')); } catch (_) {}
          try {
            if (typeof document !== 'undefined' && document.body) { void document.body.offsetHeight; }
            await new Promise((r) => requestAnimationFrame(() => r()));
            await new Promise((r) => setTimeout(r, 0));
          } catch (_) {}
          window.alert('Insufficient credit!\nGo to Account in menu to add credit.');
          return;
        }
      } catch (_) {}
      const friendlyMessage = getFriendlyServerErrorMessage(
        serverData,
        status,
        `An error occurred. Please try again.` +
        (status ? ` (HTTP ${status})` : '') +
        (err?.message ? (' F/E Error: ' + err.message) : '')
      );

      const aiMsg = {
        id: aiMsgId,
        sender: 'ai',
        content: friendlyMessage,
        htmlCode: ''
      };
      setMessages(prev => prev.map(m => (m.id === aiMsgId ? aiMsg : m)));


    } finally {
      if (requestRunIdRef.current === runId) {
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        abortControllerRef.current = null;
        isSendingRef.current = false;
        activeAiMsgIdRef.current = null;
        setActiveAiMsgId(null);
        setIsLoading(false);
        setFirstSend(false);
        // textarea already cleared on send; ensure height reset remains sane
        if (textareaRef.current) {
          try { textareaRef.current.style.height = 'auto'; } catch (_) {}
        }
      }
    }
  };




    const handleKeyDown = (e) => {
      if (e.key !== 'Enter' || isLoading) return;
      if (isMobile) return;
      if (!e.shiftKey) {
        e.preventDefault();
        handleSendStream();
      }
    };

  const handleChange = (e) => {
    setCurrentMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 24 * 6;
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
    }
  };

  const handleTextareaFocus = () => {};

  // Expose an imperative API to allow parent to trigger a send with text
  React.useImperativeHandle(ref, () => ({
    sendMessage: (text) => {
      try {
        const t = typeof text === 'string' ? text : '';
        if (!t.trim()) return;
        // Update textarea visual content
        setCurrentMessage(t);
        // Ensure textarea height adjusts
        try {
          if (textareaRef.current) {
            textareaRef.current.value = t;
            textareaRef.current.style.height = 'auto';
            const scrollHeight = textareaRef.current.scrollHeight;
            const maxHeight = 24 * 6;
            textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
          }
        } catch (_) {}
        // Defer to allow state to settle, then send
        setTimeout(() => { handleSendStream(t); }, 0);
      } catch (_) {}
    }
  }));

  const [restoreSourceId, setRestoreSourceId] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState('');

  const handleConfirmRestore = async () => {
    if (restoreSourceId === null || isRestoring || isSendingRef.current) return;
    setIsRestoring(true);
    setRestoreError('');
    try {
      const res = await axios.post(serverDomain + 'restore-to-source.php', {
        token: localStorage.getItem('appstar_jwt'),
        app_id: localStorage.getItem('appstar_app_id'),
        source_id: restoreSourceId,
      });
      if (res?.data?.success !== 1) {
        throw new Error(res?.data?.message || 'Restore failed.');
      }
      const chatMessages = Array.isArray(res.data?.data?.chatMessages) ? res.data.data.chatMessages : [];
      let html = typeof res.data?.data?.htmlSource === 'string' ? res.data.data.htmlSource : '';
      html = html.length > 0 ? html.replace(/\r\n/g, '\n') : defaultHtmlContent;
      setMessages(mapSourcesToMessages(chatMessages));
      setPreviewHtml(html);
      setSourceBuffer(html);
      setIframeKey(prev => prev + 1);
      setRestoreSourceId(null);
    } catch (err) {
      setRestoreError(err?.response?.data?.message || err?.message || 'Restore failed.');
    } finally {
      setIsRestoring(false);
    }
  };

  const lastUserMessageId = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === 'user') return messages[i].id;
    }
    return null;
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
      <div ref={chatContainerRef} data-appstar-chat-scroll="true" className="chat-messages" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: useFlowComposer ? '16px' : (usePinnedFirefoxComposer ? `16px 16px calc(${composerHeight + 16}px + var(--firefox-keyboard-offset, 0px))` : (usesFixedBottomComposer ? `16px 16px ${composerHeight + 16}px` : `16px 16px calc(${composerHeight + 16}px + var(--keyboard-inset, 0px))`)), background: theme.chatBg, overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', touchAction: 'auto' }}>
        {messages.map(m => (
          <div key={m.id} className={`chat-message-row chat-message-row-${m.sender}`} style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8, flexDirection: m.sender === 'user' ? 'row-reverse' : 'row' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 32, height: 32, borderRadius: '50%', background: theme.iconBg, color: theme.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className={m.sender === 'user' ? 'fas fa-user' : 'fas fa-robot'}></i>
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: m.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
              <div style={{ display: 'inline-block', background: m.sender === 'user' ? theme.userBubble : theme.aiBubble, color: m.sender === 'user' ? theme.userBubbleText : theme.aiBubbleText, borderRadius: m.sender === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px', padding: 12, maxWidth: '100%', textAlign: 'left', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', }}>
                <div dangerouslySetInnerHTML={{ __html: m.content }} />
                {m.htmlCode && isMobile && (
                  <div style={{ marginTop: 8 }}>
                    <button onClick={() => { setPreviewHtml(m.htmlCode); setMobileView('preview'); }} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, border: `1px solid ${theme.border}`, background: theme.inputBg, color: theme.text, cursor: 'pointer' }}>View in Preview</button>
                  </div>
                )}
              </div>
              <div
                className={`chat-bubble-actions ${m.sender === 'user' ? (isMobile ? 'is-always-visible' : 'is-hover-visible') : 'is-always-visible'}`}
                style={{ justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start' }}
              >
                {m.sender === 'user' && (
                  <button
                    type="button"
                    className="chat-bubble-action-btn"
                    title="Restore to here"
                    aria-label="Restore to here"
                    disabled={isLoading || !Number.isInteger(m.sourceId) || m.id === lastUserMessageId}
                    onClick={() => { setRestoreError(''); setRestoreSourceId(m.sourceId); }}
                  >
                    <RestoreIcon sx={{ fontSize: 18 }} />
                  </button>
                )}
                <button
                  type="button"
                  className="chat-bubble-action-btn"
                  title="Copy text"
                  aria-label="Copy text"
                  onClick={() => { void copyMessageText(m.content); }}
                >
                  <ContentCopyIcon sx={{ fontSize: 17 }} />
                </button>
              </div>
            </div>
            {m.sender === 'ai' && m.id === activeAiMsgId && isLoading && (
              <div className="typing-indicator" aria-label="AI is typing" role="status">
                <span />
                <span />
                <span />
              </div>
            )}
          </div>
        ))}
      </div>
      <div ref={composerRef} data-appstar-composer="true" style={{ borderTop: `1px solid ${theme.border}`, padding: composerPadding, display: 'flex', gap: 8, background: theme.chatBg, position: composerPosition, left: useFlowComposer ? 'auto' : 0, right: useFlowComposer ? 'auto' : 0, bottom: useFlowComposer ? 'auto' : (usePinnedFirefoxComposer ? 'var(--firefox-keyboard-offset, 0px)' : (usesFixedBottomComposer ? 0 : 'var(--keyboard-inset, 0px)')), zIndex: 3, touchAction: 'auto', flexShrink: 0 }}>
        <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'flex-end' }}>
          <textarea
            ref={(el) => { textareaRef.current = el; if (inputRef) inputRef.current = el; }}
            value={currentMessage}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleTextareaFocus}
            placeholder="What do you want to create?..."
            rows={1}
            className={showRainbowAnimation ? 'rainbow-border-animation' : ''}
            style={{ width: '100%', padding: '11px 59px 11px 17px', borderRadius: 24, border: `2px solid ${theme.inputBorder}` , outline: 'none', fontSize: isMobile ? 16 : 14, fontFamily: 'inherit', resize: 'none', overflowY: 'auto', lineHeight: '24px', height: 'auto', minHeight: '48px', maxHeight: '144px', boxSizing: 'border-box', background: theme.inputBg, color: theme.text, WebkitTextSizeAdjust: '100%' }}
          />
          {isMobile && isLoading ? (
            <>
              <span style={{ position: 'absolute', right: 53, bottom: 14, fontSize: 12, color: theme.textSecondary }}>{loadingSeconds}s</span>
              <button
                type="button"
                className="spinnerInChatButton"
                title="Stop"
                aria-label="Stop generating"
                onClick={handleStopStream}
              >
                <span
                  className="spinnerInChat spinnerInChatStop"
                  style={{ '--spinner-hole-bg': theme.inputBg }}
                  aria-hidden="true"
                >
                  <span className="spinner-hole" />
                </span>
                <span
                  className="spinner-stop-square"
                  style={{ background: isDarkMode ? '#fff' : '#000' }}
                  aria-hidden="true"
                />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={isLoading ? handleStopStream : () => handleSendStream()}
              title={isLoading ? 'Stop' : 'Send'}
              aria-label={isLoading ? 'Stop generating' : 'Send message'}
              style={{ position: 'absolute', right: 6, bottom: 7, width: 36, height: 36, borderRadius: '50%', background: (isLoading || currentMessage.trim()) ? theme.sendButtonActive : theme.sendButtonInactive, color: (isLoading || currentMessage.trim()) ? (isDarkMode ? '#222' : '#fff') : '#fff', border: 'none', cursor: (isLoading || currentMessage.trim()) ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s', padding: 0 }}
              disabled={!isLoading && !currentMessage.trim()}
            >
              {isLoading ? <Square size={13} fill={isDarkMode ? 'black' : 'white'} color={isDarkMode ? 'black' : 'white'} /> : <i className="fas fa-arrow-up" style={{ fontSize: 16, color: isDarkMode ? 'black' : 'white' }}></i>}
            </button>
          )}
        </div>
      </div>
      <Dialog
        open={restoreSourceId !== null}
        onClose={() => { if (!isRestoring) setRestoreSourceId(null); }}
        slotProps={{
          paper: { sx: { width: 360, maxWidth: '90vw', bgcolor: isDarkMode ? '#2b2b2b' : '#fff', color: theme.text, borderRadius: '10px', boxShadow: '0 10px 24px rgba(0,0,0,0.35)', backgroundImage: 'none' } },
          backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.3)' } },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>Restore to here?</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <DialogContentText sx={{ color: theme.text }}>Are you sure you want to restore to here? And chats after this will be lost.</DialogContentText>
          {restoreError && <DialogContentText sx={{ color: '#c62828', mt: 1 }}>{restoreError}</DialogContentText>}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2, pt: 0, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
          <Button
            onClick={handleConfirmRestore}
            disabled={isRestoring}
            autoFocus
            sx={{ textTransform: 'none', py: '10px', borderRadius: '6px', bgcolor: '#c62828', color: '#fff', '&:hover': { bgcolor: '#b71c1c' }, '&.Mui-disabled': { bgcolor: '#5a2a2a', color: '#aaa' } }}
          >Yes - restore to here</Button>
          <Button
            onClick={() => setRestoreSourceId(null)}
            disabled={isRestoring}
            sx={{ textTransform: 'none', py: '10px', borderRadius: '6px', bgcolor: isDarkMode ? '#424242' : '#e0e0e0', color: theme.text, '&:hover': { bgcolor: isDarkMode ? '#505050' : '#d5d5d5' }, '&.Mui-disabled': { bgcolor: isDarkMode ? '#2a2a2a' : '#eee', color: '#888' } }}
          >No - Do not restore to here</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}));

// Loading overlay with local seconds counter to avoid parent re-renders
const LoadingOverlay = React.memo(function LoadingOverlay({ isLoading }) {
  const [seconds, setSeconds] = useState(0);
  const intervalRef = useRef(null);
  useEffect(() => {
    try {
      if (isLoading) {
        setSeconds(0);
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
        intervalRef.current = setInterval(() => { setSeconds(s => s + 1); }, 1000);
      } else {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      }
    } catch (_) {}
    return () => { try { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } } catch (_) {} };
  }, [isLoading]);
  if (!isLoading) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20 }}>
      <div className="spinnerOverlayPanel" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#fff', textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }}>
        <div className="spinner spinnerOverlay" aria-hidden="true" />
        <div style={{ fontSize: 20, fontWeight: 300 }}>{seconds}s</div>
      </div>
    </div>
  );
});

function detectMobileLayout() {
  try {
    return (window.innerWidth || 0) < 769;
  } catch (_) {
    return (window.innerWidth || 0) < 768;
  }
}

function isEditableElement(element) {
  try {
    if (!element) return false;
    if (element.isContentEditable) return true;
    const tagName = String(element.tagName || '').toLowerCase();
    if (tagName === 'textarea') return true;
    if (tagName !== 'input') return false;
    const inputType = String(element.type || '').toLowerCase();
    return !['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit'].includes(inputType);
  } catch (_) {
    return false;
  }
}

function isVirtualKeyboardOpen(viewport, layoutHeight) {
  try {
    if (!viewport || !layoutHeight) return false;
    const activeElement = document.activeElement;
    if (!isEditableElement(activeElement)) return false;
    const heightDelta = Math.max(0, layoutHeight - viewport.height - (viewport.offsetTop || 0));
    return heightDelta > 120;
  } catch (_) {
    return false;
  }
}

function isFirefoxMobileBrowser() {
  try {
    const userAgent = navigator.userAgent || '';
    return /Firefox/i.test(userAgent) && /Android|Mobile|iPhone|iPad/i.test(userAgent);
  } catch (_) {
    return false;
  }
}

function usesDynamicViewportShell() {
  try {
    if (isFirefoxMobileBrowser()) return false;
    const hasCoarsePointer = typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
    return hasCoarsePointer || (navigator.maxTouchPoints || 0) > 0;
  } catch (_) {
    return false;
  }
}

function findScrollableAncestor(element) {
  let node = element;
  while (node && node !== document.body) {
    try {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
    } catch (_) {}
    node = node.parentElement;
  }
  return null;
}

export default function Chat() {
  const { isDarkMode } = useThemeMode();
  const isAppleMobileRef = useRef(false);
  const appName = localStorage.getItem('appstar_app_name') || 'AppStar';
  const [messages, setMessages] = useState(initialMessages);
  const messagesRef = useRef(initialMessages);
  const idCounterRef = useRef(1);
  const [previewHtml, setPreviewHtml] = useState();  /* messages.find(m => m.htmlCode)?.htmlCode || defaultHtmlContent */
  const [sourceBuffer, setSourceBuffer] = useState();  /* messages.find(m => m.htmlCode)?.htmlCode || defaultHtmlContent */
  const [mobileView, setMobileView] = useState('chat');
  const [viewingSource, setViewingSource] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [apiTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [firstSend, setFirstSend] = useState(true);
  const [focusPreviewOnNextLoad, setFocusPreviewOnNextLoad] = useState(false);
  const chatPaneWidth = 33.33;
  const [appIconData, setAppIconData] = useState('');
  const [appIconStyle, setAppIconStyle] = useState('');
  const [isMobile, setIsMobile] = useState(detectMobileLayout());
  const [isAppleMobile, setIsAppleMobile] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [showRainbowAnimation, setShowRainbowAnimation] = useState(false);
  const sourceEditorRef = useRef(null);
  const isEditingRef = useRef(false);

  const [consoleVisible, setConsoleVisible] = useState(false);
  const [consoleHeight, setConsoleHeight] = useState(140);
  const [consoleMessages, setConsoleMessages] = useState([]);
  const [hasConsoleError, setHasConsoleError] = useState(false);
  const [activeAppId, setActiveAppId] = useState(() => localStorage.getItem('appstar_app_id') || '');
  const chatInputRef = useRef(null);
  const chatPaneRef = useRef(null);
  const lastTouchYRef = useRef(null);

  useEffect(() => {
    try {
      const userAgent = navigator.userAgent || '';
      const platform = navigator.platform || '';
      const maxTouchPoints = navigator.maxTouchPoints || 0;
      isAppleMobileRef.current = /iPhone|iPad|iPod/i.test(userAgent)
        || (/Mac/i.test(platform) && maxTouchPoints > 1);
      setIsAppleMobile(isAppleMobileRef.current);
    } catch (_) {
      isAppleMobileRef.current = false;
      setIsAppleMobile(false);
    }
  }, []);

  const loadCurrentApp = useCallback(() => {
    try {
      console.log('Chat useEffect: initializing get-sources request');
      const jwt = localStorage.getItem('appstar_jwt');
      const app_id = localStorage.getItem('appstar_app_id');
      setMessages([]);
      setPreviewHtml(defaultHtmlContent);
      setSourceBuffer(defaultHtmlContent);
      setAppIconData('');
      setAppIconStyle('');
      setConsoleMessages([]);
      setHasConsoleError(false);
      const url = serverDomain + 'get-sources.php';
      console.log('Chat useEffect: GET', url, { app_id, token: jwt });
      axios.get(url, { params: { app_id, token: jwt } })
        .then((res) => {
          console.log('Chat useEffect: get-sources response', res);
          try {
            const chatMessages = Array.isArray(res?.data?.data?.chatMessages) ? res.data.data.chatMessages : null;
            if (chatMessages && chatMessages.length > 0) {
              const mapped = mapSourcesToMessages(chatMessages);
              //log mapped messages for debugging
              console.log('Chat onload useEffect: mapped messages', mapped);

              if (mapped.length > 0) setMessages(mapped);
            }
          } catch (_) {}
          try {
            let html = typeof res?.data?.data?.htmlSource === 'string' ? res.data.data.htmlSource : '';
            console.log('Chat useEffect: retrieved html source', html); 
            //if the htmlSource key from response contains non-empty string, use it as the initial previewHtml and sourceBuffer, else use defaultHtmlContent (i.e. the start screen with AppStar logo) ...
            if (html.length > 0) { 
              // Preserve escaped sequences (e.g. /[\r\n]/) as-is; only normalize real CRLF pairs.
              html = String(html).replace(/\r\n/g, '\n');
              
              setPreviewHtml(html); 
              setSourceBuffer(html); 
              setIframeKey(prev => prev + 1); 
            } else {
              setPreviewHtml(defaultHtmlContent); 
              setSourceBuffer(defaultHtmlContent); 
              setIframeKey(prev => prev + 1);
            }
          } catch (_) {}
        })
        .catch((err) => { console.error('Chat useEffect: get-sources error', err); });

      if (jwt && app_id) {
        axios
          .post(serverDomain + 'get-all-apps.php', null, { params: { token: jwt } })
          .then((res) => {
            const apps = res?.data?.data?.apps || [];
            const currentApp = apps.find((app) => String(app.app_id) === String(app_id));
            if (!currentApp) return;
            setAppIconData(currentApp.icon_data || '');
            setAppIconStyle(currentApp.icon_style || '');
          })
          .catch((err) => {
            console.error('Chat useEffect: get-all-apps error', err);
          });
      }
      } catch (err) { console.error('Chat useEffect: setup failed', err); }
  }, [setMessages]);

  useEffect(() => {
    loadCurrentApp();
  }, [activeAppId, loadCurrentApp]);

  useEffect(() => {
    const handleAppSelected = (event) => {
      const nextAppId = event?.detail?.appId || localStorage.getItem('appstar_app_id') || '';
      setActiveAppId(String(nextAppId));
    };

    window.addEventListener('appstar:app-selected', handleAppSelected);

    return () => {
      window.removeEventListener('appstar:app-selected', handleAppSelected);
    };
  }, []);

  useEffect(() => {
    const syncViewportMetrics = () => {
      try {
        const viewport = window.visualViewport;
        const layoutHeight = window.innerHeight;
        const useDynamicViewport = isAppleMobileRef.current || usesDynamicViewportShell();
        const isFirefoxMobile = isFirefoxMobileBrowser();
        const keyboardOpen = isVirtualKeyboardOpen(viewport, layoutHeight);
        const viewportHeight = viewport ? viewport.height : layoutHeight;
        const rawKeyboardInset = viewport
          ? Math.max(0, layoutHeight - viewport.height - (viewport.offsetTop || 0))
          : 0;
        setIsKeyboardOpen(keyboardOpen);
        const keyboardInset = !useDynamicViewport && !isFirefoxMobile && keyboardOpen ? rawKeyboardInset : 0;
        const firefoxKeyboardOffset = isFirefoxMobile && keyboardOpen
          ? Math.max(0, rawKeyboardInset - 1)
          : 0;
        const appHeight = useDynamicViewport && keyboardOpen && !isFirefoxMobile ? viewportHeight : layoutHeight;
        document.documentElement.style.setProperty('--app-height', `${appHeight}px`);
        document.documentElement.style.setProperty('--keyboard-inset', `${keyboardInset}px`);
        document.documentElement.style.setProperty('--firefox-keyboard-offset', `${firefoxKeyboardOffset}px`);
      } catch (_) {}
    };

    const handleResize = () => {
      setIsMobile(detectMobileLayout());
      syncViewportMetrics();
    };

    syncViewportMetrics();

    const viewport = window.visualViewport;
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', syncViewportMetrics, { passive: true });
    window.addEventListener('orientationchange', syncViewportMetrics);
    if (viewport) {
      viewport.addEventListener('resize', syncViewportMetrics);
      viewport.addEventListener('scroll', syncViewportMetrics);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', syncViewportMetrics);
      window.removeEventListener('orientationchange', syncViewportMetrics);
      if (viewport) {
        viewport.removeEventListener('resize', syncViewportMetrics);
        viewport.removeEventListener('scroll', syncViewportMetrics);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobile && isFullscreen) {
      setIsFullscreen(false);
    }
  }, [isMobile, isFullscreen]);

  useEffect(() => {
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehaviorY = 'none';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const onTouchStart = (e) => {
      try {
        lastTouchYRef.current = e.touches && e.touches.length > 0 ? e.touches[0].clientY : null;
      } catch (_) {
        lastTouchYRef.current = null;
      }
    };
    const onTouchMove = (e) => {
      if (!e.touches || e.touches.length === 0) return;
      const touch = e.touches[0];
      const target = e.target;
      const insideChatScroller = typeof target?.closest === 'function' && !!target.closest('[data-appstar-chat-scroll="true"]');
      const insideComposer = typeof target?.closest === 'function' && !!target.closest('[data-appstar-composer="true"]');
      const viewport = window.visualViewport;
      const keyboardOpen = isVirtualKeyboardOpen(viewport, window.innerHeight);

      const scrollableAncestor = findScrollableAncestor(target);
      if (scrollableAncestor) {
        if (insideChatScroller && keyboardOpen) {
          const previousTouchY = lastTouchYRef.current;
          lastTouchYRef.current = touch.clientY;
          if (previousTouchY == null) return;

          const deltaY = touch.clientY - previousTouchY;
          const maxScrollTop = Math.max(0, scrollableAncestor.scrollHeight - scrollableAncestor.clientHeight);
          const atTop = scrollableAncestor.scrollTop <= 0;
          const atBottom = scrollableAncestor.scrollTop >= maxScrollTop - 1;
          const pullingPastTop = deltaY > 0 && atTop;
          const pullingPastBottom = deltaY < 0 && atBottom;

          if (pullingPastTop || pullingPastBottom) {
            e.preventDefault();
          }
          return;
        }
        lastTouchYRef.current = touch.clientY;
        return;
      }
      if (insideChatScroller) return;
      if (keyboardOpen && insideComposer) {
        e.preventDefault();
        return;
      }
      if (keyboardOpen) {
        e.preventDefault();
        return;
      }
      if (window.scrollY === 0 && touch.clientY > 0) e.preventDefault();
    };
    const clearTouchTracking = () => { lastTouchYRef.current = null; };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', clearTouchTracking, { passive: true });
    document.addEventListener('touchcancel', clearTouchTracking, { passive: true });
    return () => {
      document.documentElement.style.removeProperty('overscroll-behavior');
      document.documentElement.style.removeProperty('overscroll-behavior-y');
      document.body.style.removeProperty('overscroll-behavior');
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', clearTouchTracking);
      document.removeEventListener('touchcancel', clearTouchTracking);
    };
  }, []);

  // Trigger rainbow border animation once on page load
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowRainbowAnimation(true);
      // Remove animation after 3 seconds
      const removeTimer = setTimeout(() => {
        setShowRainbowAnimation(false);
        // Avoid auto-focusing the chat input on mobile
        try { if (chatInputRef.current && !isMobile) chatInputRef.current.focus({ preventScroll: true }); } catch (_) {}
      }, 3000);
      return () => clearTimeout(removeTimer);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const theme = {
    bg: isDarkMode ? '#1a1a1a' : '#f5f6fa',
    chatBg: isDarkMode ? '#0d0d0d' : '#fafbfc',
    text: isDarkMode ? '#e0e0e0' : '#222',
    textSecondary: '#888',
    userBubble: isDarkMode ? '#e0e0e0' : '#222',
    userBubbleText: isDarkMode ? '#222' : '#fff',
    aiBubble: isDarkMode ? '#2a2a2a' : '#eee',
    aiBubbleText: isDarkMode ? '#e0e0e0' : '#222',
    iconBg: isDarkMode ? '#2a2a2a' : '#eee',
    iconColor: isDarkMode ? '#e0e0e0' : '#222',
    border: isDarkMode ? '#333' : '#eee',
    inputBg: isDarkMode ? '#1a1a1a' : '#fff',
    inputBorder: isDarkMode ? '#444' : '#ccc',
    buttonBg: isDarkMode ? '#555' : '#444',
    buttonText: '#fff',
    buttonBorder: isDarkMode ? '#666' : '#888',
    previewBg: '#fff',
    sourceBg: isDarkMode ? '#1a1a1a' : '#f8f8f8',
    sourceContainerBg: isDarkMode ? '#000' : '#fff',
    headerBg: isDarkMode ? '#000' : '#003366',
    headerText: '#fff',
    sendButtonActive: isDarkMode ? '#e0e0e0' : '#222',
    sendButtonInactive: '#ccc'
  };

  useEffect(() => {
    const lastLoggedErrorRef = { current: null };
    window.logConsoleError = (message, line, col) => {
      try {
        const key = `${String(message)}::${line ?? ''}::${col ?? ''}`;
        const now = Date.now();
        const last = lastLoggedErrorRef.current;
        if (last && last.key === key && (now - last.time) < 500) return;
        lastLoggedErrorRef.current = { key, time: now };
        setConsoleMessages(prev => [...prev, { message: String(message), line: line ?? null, col: col ?? null }]);
        setHasConsoleError(true);
      } catch (err) { console.error('logConsoleError failed', err); }
    };
    function onMsg(ev){ try{ const d = ev && ev.data; if (!d || d.type !== 'appstar-console-error') return; const p = d.payload || {}; window.logConsoleError(p.msg, p.line, p.col); }catch(_){}}
    window.addEventListener('message', onMsg, false);
    return () => { window.logConsoleError = undefined; window.removeEventListener('message', onMsg, false); };
  }, []);

  useEffect(() => { if (consoleMessages.length > 0) { setConsoleMessages([]); setHasConsoleError(false); } }, [previewHtml]);

  /* 
    This function is no longer used since message addition is handled directly in handleSend2
    const addMessage = (msg, aiMsg) => {
    setMessages(msgs => [...msgs, msg, aiMsg]);
    setSourceBuffer(aiMsg.htmlCode);
    setPreviewHtml(aiMsg.htmlCode);
  }; */

  const desktopBtnStyle = { padding: '0.rem 0.6rem', background: theme.buttonBg, color: theme.buttonText, border: `1px solid ${theme.buttonBorder}`, borderRadius: 4, fontWeight: 500, fontSize: 14, cursor: 'pointer', minWidth: 64, position: 'relative', zIndex: 10 };

  const DesktopButtons = () => (
    <div style={{ display: isMobile ? 'none' : 'flex', gap: 8, marginLeft: 16, position: 'relative', zIndex: 10 }}>
      <button style={desktopBtnStyle} title="Restart app" onClick={() => { if (consoleMessages.length > 0 || hasConsoleError) { setConsoleMessages([]); setHasConsoleError(false); } setFocusPreviewOnNextLoad(true); setIframeKey(prev => prev + 1); }}>ReRun</button>
      <button style={desktopBtnStyle} title={viewingSource ? 'View App' : 'View Source'} onClick={() => { if (viewingSource) { const content = sourceBuffer || previewHtml || ''; setPreviewHtml(content); } else { setSourceBuffer(((previewHtml || '')).replace(/\r\n/g, '\n')); } setViewingSource(!viewingSource); }}>{viewingSource ? 'View App' : 'View Source'}</button>
      <button style={desktopBtnStyle} title={consoleVisible ? 'Hide console' : 'Show console'} onClick={() => setConsoleVisible(v => !v)}>Console<span style={{ display: hasConsoleError ? 'inline-block' : 'none', marginLeft: 8 }}><span style={{ width: 10, height: 10, background: '#ff4d4f', borderRadius: '50%', display: 'inline-block' }} /></span></button>
      <button style={{ ...desktopBtnStyle, minWidth: 'auto', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} onClick={() => setIsFullscreen(!isFullscreen)}>{isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}</button>
    </div>
  );

  const MobileToggleButton = () => {
    if (!isMobile) return null;
    const squareBtn = { width: 35, height: 35, padding: 4, background: theme.buttonBg, color: theme.buttonText, border: `1px solid ${theme.buttonBorder}`, borderRadius: 6, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 10 };
    return (
      <div style={{ display: 'flex', gap: 6, marginLeft: 8, position: 'relative', zIndex: 10 }}>
        {mobileView === 'preview' && !viewingSource && (
          <button style={squareBtn} title="Restart app" onClick={() => { if (consoleMessages.length > 0 || hasConsoleError) { setConsoleMessages([]); setHasConsoleError(false); } setFocusPreviewOnNextLoad(true); setIframeKey(prev => prev + 1); }}><i className="fas fa-play" /></button>
        )}
        {mobileView === 'preview' && (
          <button style={squareBtn} title={viewingSource ? 'View App' : 'View Source'} onClick={() => { if (viewingSource) { const content = sourceBuffer || previewHtml || ''; setPreviewHtml(content); } else { setSourceBuffer(((previewHtml || '')).replace(/\r\n/g, '\n')); } setViewingSource(!viewingSource); }}>
            <i className={viewingSource ? 'fas fa-window-maximize' : 'fas fa-code'} />
          </button>
        )}
        {mobileView === 'preview' && !viewingSource && (
          <button style={{ ...squareBtn, background: hasConsoleError ? '#ff4d4f22' : squareBtn.background, border: `1px solid ${hasConsoleError ? '#ff4d4f' : theme.buttonBorder}`, boxShadow: consoleVisible ? 'inset 0 0 0 1px rgba(255,255,255,0.2)' : 'none' }} title={consoleVisible ? 'Hide console' : 'Show console'} onClick={() => setConsoleVisible(v => !v)}>
            <i className="fas fa-terminal" />
          </button>
        )}
        <button
          style={{ ...squareBtn, order: mobileView === 'preview' ? 99 : 0 }}
          title={mobileView === 'chat' ? 'Preview' : 'Chat'}
          onClick={() => {
            const nextView = mobileView === 'chat' ? 'preview' : 'chat';
            if (isMobile && nextView === 'chat' && isFullscreen) {
              setIsFullscreen(false);
            }
            setMobileView(nextView);
          }}
        >
          <i className={mobileView === 'chat' ? 'fas fa-eye' : 'fas fa-comments'} />
        </button>
      </div>
    );
  };

  

  function injectErrorCatcher(htmlString) {
  const script = `\n<script>(function(){\n  function send(data){\n    try { parent.postMessage({ type: 'appstar-console-error', payload: data }, '*'); } catch(_){}\n    try { if (parent && parent.logConsoleError) parent.logConsoleError(data.msg, data.line, data.col); } catch(_){}\n  }\n  window.addEventListener('error', function(e){ try{ send({ msg: (e && e.message) ? e.message : String(e), line: e.lineno || (e.error && e.error.lineNumber) || null, col: e.colno || null }); }catch(_){} }, true);\n  window.addEventListener('unhandledrejection', function(e){ try{ var r = e && e.reason ? (e.reason.message || e.reason) : e; send({ msg: r, line: null, col: null }); }catch(_){} }, true);\n})();</script>\n`;
    //Shows warnings: const script = `\n<script>(function(){\n  function sendError(data){\n    try { parent.postMessage({ type: 'appstar-console-error', payload: data }, '*'); } catch(_){}\n    try { if (parent && parent.logConsoleError) parent.logConsoleError(data.msg, data.line, data.col); } catch(_){}\n  }\n\n  // Forward console.error / console.warn so library-handled errors appear in the pane\n  try {\n    var origError = console.error && console.error.bind(console);\n    var origWarn = console.warn && console.warn.bind(console);\n    if (origError) {\n      console.error = function(){\n        try {\n          var msg = Array.prototype.slice.call(arguments).map(function(a){ return (a && a.stack) ? a.stack : String(a); }).join(' ');\n          sendError({ msg: msg, line: null, col: null });\n        } catch(_){}\n        return origError.apply(console, arguments);\n      };\n    }\n    if (origWarn) {\n      console.warn = function(){\n        try {\n          var msg = Array.prototype.slice.call(arguments).map(function(a){ return (a && a.stack) ? a.stack : String(a); }).join(' ');\n          sendError({ msg: msg, line: null, col: null });\n        } catch(_){}\n        return origWarn.apply(console, arguments);\n      };\n    }\n  } catch(_){}\n\n  // Catch resource load errors (script/img/link)\n  window.addEventListener('error', function(e){\n    try{\n      var t = e.target || e.srcElement;\n      if (t && t.tagName && (t.tagName === 'SCRIPT' || t.tagName === 'IMG' || t.tagName === 'LINK')) {\n        var url = t.src || t.href || '';\n        sendError({ msg: 'Resource load error on ' + t.tagName + ': ' + url, line: null, col: null });\n        return;\n      }\n      sendError({ msg: (e && e.message) ? e.message : String(e), line: e.lineno || (e.error && e.error.lineNumber) || null, col: e.colno || null });\n    }catch(_){}\n  }, true);\n\n  // Catch unhandled promise rejections\n  window.addEventListener('unhandledrejection', function(e){\n    try{ var r = e && e.reason ? (e.reason.stack || e.reason.message || e.reason) : e; sendError({ msg: r, line: null, col: null }); }catch(_){}\n  }, true);\n})();</script>\n`;
    try {
      const headOpenRe = /<head[^>]*>/i; if (headOpenRe.test(htmlString)) return htmlString.replace(headOpenRe, match => match + script);
      const headCloseRe = /<\/head\s*>/i; if (headCloseRe.test(htmlString)) return htmlString.replace(headCloseRe, script + '</head>');
      const doctypeRe = /^(\s*<!doctype[^>]*>\s*)/i; if (doctypeRe.test(htmlString)) return htmlString.replace(doctypeRe, (m) => m + script);
      const htmlOpenRe = /<html[^>]*>/i; if (htmlOpenRe.test(htmlString)) return htmlString.replace(htmlOpenRe, match => match + script);
      return script + htmlString;
    } catch (err) { console.error('injectErrorCatcher failed:', err); return htmlString; }
  }

  const injectedHtml = React.useMemo(() => injectErrorCatcher(previewHtml || '<!doctype html><html><body></body></html>'), [previewHtml]);

  // (No global interval cleanup needed; LoadingOverlay manages its own interval.)

  return (
    <div style={{ height: isMobile ? 'auto' : 'var(--app-height, 100dvh)', background: theme.bg, display: 'flex', flexDirection: 'column', position: isMobile ? 'fixed' : 'relative', top: isMobile ? 0 : 'auto', left: isMobile ? 0 : 'auto', right: isMobile ? 0 : 'auto', bottom: isMobile ? 0 : 'auto', width: isMobile ? '100%' : 'auto', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', background: theme.headerBg, color: theme.headerText, padding: '0 8px 0 0', borderBottom: `1px solid ${theme.border}` }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
          <AdminBar bgColor={theme.headerBg} textColor={theme.headerText} appName={appName} appIconData={appIconData} appIconStyle={appIconStyle} chatPaneWidth={chatPaneWidth} isMobile={isMobile} isFullscreen={isFullscreen} fixed={false} />
        </div>
        <DesktopButtons />
        <MobileToggleButton />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: 0, overflow: 'hidden' }}>
        <div style={{ display: (isMobile && mobileView !== 'chat') || isFullscreen ? 'none' : 'flex', width: isMobile ? '100%' : (isFullscreen ? '0%' : `${chatPaneWidth}%`), minWidth: 0, borderRight: !isMobile && !isFullscreen ? `1px solid ${theme.border}` : 'none', background: theme.chatBg, height: '100%', minHeight: 0, flexDirection: 'column', flex: isMobile && mobileView === 'chat' ? 1 : 'none' }}>
          <ChatPane
            ref={chatPaneRef}
            setPreviewHtml={setPreviewHtml}
            showRainbowAnimation={showRainbowAnimation}
            inputRef={chatInputRef}
            messages={messages}
            setMessages={setMessages}
            idCounterRef={idCounterRef}
            previewHtml={previewHtml}
            sourceBuffer={sourceBuffer}
            setSourceBuffer={setSourceBuffer}
            firstSend={firstSend}
            setFirstSend={setFirstSend}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            setIframeKey={setIframeKey}
            isMobile={isMobile}
            isAppleMobile={isAppleMobile}
            isKeyboardOpen={isKeyboardOpen}
            setMobileView={setMobileView}
            theme={theme}
            apiTimer={apiTimer}
            isDarkMode={isDarkMode}
          />
        </div>
        <div style={{ display: isMobile && mobileView === 'chat' ? 'none' : 'flex', flexDirection: 'column', width: isMobile ? '100%' : (isFullscreen ? '100%' : `${100 - chatPaneWidth}%`), minWidth: 0, height: '100%', minHeight: 0 }}>
          <div style={{ display: (isMobile && mobileView !== 'preview') ? 'none' : (viewingSource ? 'none' : 'flex'), flex: 1, minHeight: 0, minWidth: 0, flexDirection: 'column', position: 'relative' }}>
            <PreviewPane injectedHtml={injectedHtml} iframeKey={iframeKey} previewBg={theme.previewBg} onIframeLoad={(e) => { try { if (!isMobile) { if (focusPreviewOnNextLoad) { e && e.target && e.target.focus && e.target.focus({ preventScroll: true }); setFocusPreviewOnNextLoad(false); } else if (chatInputRef.current) { chatInputRef.current.focus({ preventScroll: true }); } } } catch (_) {} }} />
            <LoadingOverlay isLoading={isLoading} />
          </div>
          <div style={{ display: (isMobile && mobileView !== 'preview') || !viewingSource ? 'none' : 'flex', flex: 1, minHeight: 0, minWidth: 0, flexDirection: 'column', position: 'relative' }}>
            <SourceView theme={theme} isDarkMode={isDarkMode} viewingSource={viewingSource} sourceBuffer={sourceBuffer} setSourceBuffer={setSourceBuffer} sourceEditorRef={sourceEditorRef} isEditingRef={isEditingRef} />
            <LoadingOverlay isLoading={isLoading} />
          </div>
          {consoleVisible && (
            <div style={{ flexShrink: 0 }}>
              <ConsolePane
                height={consoleHeight}
                onHeightChange={setConsoleHeight}
                messages={consoleMessages}
                onClear={() => { setConsoleMessages([]); setHasConsoleError(false); }}
                onFix={(text) => {
                  try {
                    const raw = typeof text === 'string' ? text : '';
                    const t = raw.trim() ? `Fix this error: ${raw}` : '';
                    if (!t.trim()) return;
                    if (isMobile) setMobileView('chat');
                    setTimeout(() => { try { chatPaneRef.current && chatPaneRef.current.sendMessage(t); } catch (_) {} }, 0);
                  } catch (_) {}
                }}
                hasError={hasConsoleError}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
